import { AsyncPipe, NgClass } from '@angular/common';
import { Component, Input, NgZone, OnDestroy, OnInit, inject } from '@angular/core';

import {
    Activation,
    CartModel,
    Delivery,
    Edp,
    Equipment,
    Fai,
    FaiBox,
    IOptionData,
    Installation,
    OptionSalable,
    PRICE_TYPES,
    Phone,
    Plan,
    Product,
    ProductFactory,
    PromotionModel,
    PromotionsService,
    QUOTE_CONTEXTS,
    QuoteModel,
    TYPE_MARKETING,
} from '@bytel/bytel-sales';
import {
    CheckoutStorageService,
    CommercialProposal,
    CommercialSeries,
    FUNDING_ENUM,
    FundingMethod,
    FundingService,
    GalliumService,
    Oauth2Service,
    QuoteContextRenewalModel,
    SHIPPING_MODES,
    TotalsDetail,
} from '@bytel/checkout';

import { DataCyDirective } from '@base/common/directives/data-cy.directive';
import { ScrollToDirective } from '@base/common/directives/scrollTo.directive';

import { AppService } from '@services/app.services';
import { ConfigurationService } from '@services/configuration.service';
import { MainCartService } from '@services/main-cart.service';
import { StepperService } from '@services/stepper.service';
import { TicketPricesFactory } from '@services/ticket-prices.factory';

import { IFormStatus, ProductLine } from '@interfaces/checkout.interface';

import { PaymentStep } from '@shared/stepper/steps/payment.step';

import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { QuoteHelper } from '../../helper/quote-helper';
import { DeliveryBaseService } from '../../services/delivery/delivery-base.service';
import { FaiService } from '../../services/fai.services';
import { MixedCartService } from '../../services/mixed-cart.service';
import { PromoHomeService } from '../../tunnel/cart/subscription-block/promo-home.service';
import { MobileRecoveryComponent } from '../mobile-recovery/mobile-recovery.component';
import { PriceInitialPipe } from '../pipes/price-initial.pipe';
import { ProductLineTypePipe } from '../pipes/product-line-type.pipe';
import { CouponComponent } from '../promotion/coupon/coupon.component';
import { StickyComponent } from './sticky/sticky.component';
import { TicketEmptyComponent } from './ticket-empty/ticket-empty.component';
import { TicketlineComponent } from './ticketline/ticketline.component';

@Component({
    selector: 'checkout-ticket',
    templateUrl: './ticket.component.html',
    styleUrl: 'ticket.component.scss',
    standalone: true,
    imports: [
        NgClass,
        ScrollToDirective,
        DataCyDirective,
        MobileRecoveryComponent,
        CouponComponent,
        AsyncPipe,
        ProductLineTypePipe,
        TicketlineComponent,
        StickyComponent,
        TicketEmptyComponent,
    ],
})
export class TicketComponent implements OnInit, OnDestroy {
    @Input() public noButtons: boolean = false;
    public couponPromotions: Product[] = [];
    public equipment: Equipment;
    public funding: FundingMethod = null;
    public commercialProposal: CommercialProposal = null;
    public plan: Plan;
    public isAcquisitionFix: boolean;
    public isCartEmpty: boolean = false;
    public isMobileMQ: boolean = false;
    public showDaily: boolean = false;
    public showMonthly: boolean = false;
    public showReported: boolean = false;
    public ticketLines: ProductLine[] = [];
    public optionsTicketLines: ProductLine[] = [];
    public accessoriesTicketLines: ProductLine[] = [];
    public mixedCartMobileOptions: ProductLine[] = [];
    public mixedCartFaiOptions: ProductLine[] = [];
    public edpTicketLine: ProductLine = null;
    public scrollToTicket: boolean = false;
    public showFundingPrices = true;
    public hasMobileRecovery: boolean = false;
    public isMixedCart: boolean = false;
    public isFaiFirst: boolean = false;
    public totals: TotalsDetail = {
        daily: {
            base: 0,
            final: 0,
        },
        monthly: {
            base: 0,
            final: 0,
        },
        reported: {
            base: 0,
            final: 0,
        },
    };
    public isPreorder: boolean = false;
    public refundAmount: number;
    public fundingPartner: string;
    public fundingSubtitle: string[] = [];
    public formStatus$: Observable<IFormStatus>;
    public monthlyDurationLabel: string = null;
    public validateButtonLabels = {
        DEFAULT: 'Continuer',
        PAYMENT: 'Valider et payer',
    };
    public validateButtonLabel = this.validateButtonLabels.DEFAULT;
    public showDividerBeforeCoupon: boolean = false;
    public isRenewal: boolean = false;
    public milanoPromoAmount: number = 0;
    public promoHome: PromotionModel;
    public showInfoPrice: boolean = true;
    public showAccessoriesInDaily: boolean = false;
    public showAccessoriesInFundings: boolean = false;
    public showMobileOptions: boolean = false;
    public showFaiOptions: boolean = false;
    public showAllOptions: boolean = false;

    protected cartService: MainCartService = inject(MainCartService);
    protected stepperService: StepperService = inject(StepperService);
    protected appService: AppService = inject(AppService);
    protected configurationService: ConfigurationService = inject(ConfigurationService);

    private fmsToPayToday: boolean = false;
    private ngZone: NgZone = inject(NgZone);
    private priceInitialPipe: PriceInitialPipe = inject(PriceInitialPipe);
    private fundingService: FundingService = inject(FundingService);
    private mixedCartService: MixedCartService = inject(MixedCartService);

    private _cartQuoteSubscription: Subscription;
    private _paymentSubscription: Subscription;
    private readonly MOBILE_BREAKPOINT: string = '(max-width: 1023px)';
    private _ngUnsubscribe$ = new Subject();
    private galliumService: GalliumService = inject(GalliumService);
    private deliveryBaseService = inject(DeliveryBaseService);

    private storageService: CheckoutStorageService = inject(CheckoutStorageService);
    private oauth2Service: Oauth2Service = inject(Oauth2Service);
    private faiService: FaiService = inject(FaiService);
    private promoHomeService: PromoHomeService = inject(PromoHomeService);

    public ngOnInit(): void {
        this._initBreakpointMonitoring();
        this.isCartEmpty = this.cartService.cartModel.isCartEmpty();

        this._initFunding();
        this.checkQuoteRenewal();

        this._cartQuoteSubscription = this.cartService.cartQuoteSubscription.subscribe(() => {
            const quote = this.cartService.getQuote();
            const quote1 = this.cartService.getQuote(1);
            this.equipment = quote.getPrincipalProduct('Equipment');
            const plan = quote.getPrincipalProduct('Plan');
            this.isAcquisitionFix = quote.isAcquisitionFix();
            this.plan = { ...plan } as Plan; // new ref for pipe
            this.funding = this.cartService.cartModel.fundingMethod;
            this._refresh();
            this.isCartEmpty = this.cartService.cartModel.isCartEmpty();
            this.isPreorder = quote.isPreorder();
            this.fundingSubtitle[0] = this.fundingPartner ? `Prélevé par ${this.fundingPartner}` : '';
            this._getPromoAmount();
            const faiPlan: Fai = quote?.getPrincipalProduct(Fai) || quote1?.getPrincipalProduct(Fai);
            if (faiPlan) {
                this.fmsToPayToday = this.faiService.isFmsToPayTodayByCommercialSerie(
                    faiPlan.data.commercial_series as CommercialSeries,
                );
            }
        });

        this._paymentSubscription = this.cartService.paymentMethodObs.subscribe(() => {
            this._initFunding();

            this._refresh();
        });

        this.formStatus$ = this.appService.formStatusSubject;

        this.stepperService.currentStepSubject.pipe(takeUntil(this._ngUnsubscribe$)).subscribe((step) => {
            this.validateButtonLabel =
                step.key === PaymentStep.key && step.isActive
                    ? this.validateButtonLabels.PAYMENT
                    : this.validateButtonLabels.DEFAULT;
        });
    }

    public ngOnDestroy(): void {
        this._cartQuoteSubscription.unsubscribe();
        this._paymentSubscription.unsubscribe();
        this._ngUnsubscribe$.next(true);
        this._ngUnsubscribe$.unsubscribe();
    }

    public receiveScroll(): void {
        this.scrollToTicket = false;
    }

    public validate(): void {
        this.stepperService.validateSubject.next(true);
    }

    public showRecoveryData(display: boolean): void {
        this.hasMobileRecovery = display;
    }

    private _getTicketLinesByProductType(types: string[]): ProductLine[] {
        return this.ticketLines?.filter((product) => types.includes(product.productType));
    }

    private _initFunding(): void {
        this.funding = this.cartService.cartModel.fundingMethod;
        if (this.funding) {
            if (this.funding?.type === FUNDING_ENUM.CASH || this.funding?.type === FUNDING_ENUM.MULTI_PRODUCT) {
                this.commercialProposal = null;
            } else {
                this.commercialProposal =
                    this.funding?.type === FUNDING_ENUM.YOUNITED
                        ? this.fundingService.getYounitedSelectedProposal(this.funding)
                        : this.fundingService.getCofidisSelectedProposal(this.funding);
                this.fundingPartner = this.funding?.type === FUNDING_ENUM.YOUNITED ? 'Younited' : 'Cofidis Pay';
            }
        } else {
            this.commercialProposal = null;
            this.fundingPartner = null;
        }
    }

    /**
     * Update price totals
     *
     * @private
     * @memberof TicketComponent
     */
    private _refresh(): void {
        this.isMixedCart = this.mixedCartService.isMixedCart(this.cartService.cartModel);
        this.isFaiFirst = this.isMixedCart && this.cartService.cartModel.quotes[0].isAcquisitionFix();
        this.ticketLines = [];
        this.edpTicketLine = null;
        this.couponPromotions = [];
        this.refundAmount = this._getRefundAmount();
        this.showDaily = this.showMonthly = this.showReported = false;
        this._setMonthlyDurationLabel();
        this._setTicketLines();
        this.showDividerBeforeCoupon = !this.ticketLines.some((ticketLine) => ticketLine.type === 'coupon');
        this.showFundingPrices = !!(this.commercialProposal?.nbLoan || this.edpTicketLine?.price);
        this.totals = this._getTotals(this.cartService.cartModel);

        if (this.commercialProposal?.nbLoan) {
            this.totals.daily.final = this.commercialProposal?.monthlyAmount;
        }

        if (this.edpTicketLine && this.funding?.label === 'MULTI_PRODUCT') {
            this.totals.monthly.final -= this.edpTicketLine.price;
        }
    }

    private getDeliveryInfoForTicketLine(product: Product): string {
        if (!this.isMixedCart) {
            return '';
        }
        return product.gencode === SHIPPING_MODES.LA_POSTE ? '(sim)' : '(box)';
    }
    private _setTicketLines(): void {
        const products = this._getProductsTicket();
        products.forEach((product) => {
            const qty: number = this.cartService.getProductQty(product);
            const isDeliveryProduct = ProductFactory.Is(product, Delivery);
            const productInfo: ProductLine = {
                qty,
                label: isDeliveryProduct
                    ? this.deliveryBaseService.improveLabelDisplay(
                          product.label,
                          this.getDeliveryInfoForTicketLine(product),
                      )
                    : product.label,
                price: TicketPricesFactory.GetPrice(product, qty),
                type: null,
                extraInfo: product.data.wording_promo,
                popover: null,
                gencode: product.gencode,
                subtitles: [],
                periodText: null,
                periodPrice: null,
                productType: product.data.type_id,
                isIncludedPrice: false,
            };

            this._setEdpTicketLine(product, productInfo);
            this._setPlanTicketLine(product, productInfo);

            if (product.priceType === PRICE_TYPES.Today) {
                this.showDaily = true;
                productInfo.type = 'daily';
                productInfo.popover = this._getPopoverLabel(product);
            }

            if (product.priceType === PRICE_TYPES.EveryMonth) {
                this.showMonthly = true;
                productInfo.type = 'monthly';
                productInfo.popover = this._getPopoverLabel(product);
            }

            if (product.priceType === PRICE_TYPES.Reported) {
                this.showReported = true;
                productInfo.type = 'reported';
                productInfo.popover = this._getPopoverLabel(product);
            }

            if (ProductFactory.Is(product, 'Insurance')) {
                productInfo.subtitles[0] = { label: 'Prélevée par SPB' };
            }

            // Temp popover for fake option debit+
            if (ProductFactory.Is(product, OptionSalable) && product.gencode === '72156945') {
                productInfo.popover = `En attendant la réception de Débit +, vous profitez de la fibre très rapide : jusqu'à 2Gb/s et 900 Mbt/s`;
            }

            if (
                this.commercialProposal?.nbLoan > 0 &&
                productInfo.productType !== 'phone_simple' &&
                productInfo.type === 'daily'
            ) {
                productInfo.isIncludedPrice = true;
            }

            this.ticketLines.push(productInfo);

            this._setTicketLinePromo(product, qty);

            this._checkFmsToPayToday(product);
        });

        this.refreshOptionsAndAccessories();
    }

    private _setPlanTicketLine(product: Product, productInfo: ProductLine): void {
        if (ProductFactory.Is(product, Plan)) {
            const isConnected = this.oauth2Service.isConnected;

            this._handleHomePromo(isConnected, product, productInfo);
            this._handlePromoDuration(product, productInfo);
            this._handleRenewal(product, productInfo);

            product.promotions?.automatic?.forEach((promo) => {
                this._processAutomaticPromotions(promo, productInfo);
            });
        }
    }

    private _handleRenewal(product: Product, productInfo: ProductLine): void {
        if (this.isRenewal) {
            const galliumData = this.galliumService.getGalliumData(
                product.data.gencode,
                (this.cartService.getQuote(0).context as QuoteContextRenewalModel).contractId,
                !this.cartService.isMigratingPlan(),
            );
            productInfo.label = galliumData?.libelleCommercial || productInfo?.label;
        }
    }

    private _processAutomaticPromotions(promo: PromotionModel, productInfo: ProductLine): void {
        if (!promo.duration && promo.typesMarketing.includes(TYPE_MARKETING.FOYER)) {
            productInfo.subtitles.push({
                label: promo.name ? `${promo.name} déjà déduits` : 'Remise déduite',
                price: -promo.amount,
            });
        }
    }

    private _handlePromoDuration(product: Product, productInfo: ProductLine): void {
        if (product.prices.duration) {
            productInfo.promoDuration = product.prices.duration;
            productInfo.periodText = `Pendant ${product.prices.duration} mois puis`;
            productInfo.periodPrice =
                TicketPricesFactory.GetDurationPrice(product) -
                (!this.isMixedCart && this.promoHome?.amount ? this.promoHome?.amount : 0);
        }
    }

    private _handleHomePromo(isConnected: boolean, product: Plan, productInfo: ProductLine): void {
        if (isConnected && this.promoHome?.amount > 0) {
            this.promoHome = null;
            return;
        }
        if (!isConnected) {
            this.promoHome = this.promoHomeService.getApplicablePromoHome(
                product,
                this.cartService.cartModel.promotions,
                false,
            );
            if (
                this.promoHome &&
                !product?.promotions.automatic?.find((promo) => promo.typesMarketing.includes(TYPE_MARKETING.FOYER))
            ) {
                productInfo.price -= this.promoHome?.amount;
                productInfo.subtitles.push({
                    label: this.promoHome.name ? `${this.promoHome.name} déjà déduits` : 'Remise déduite',
                    price: -this.promoHome?.amount,
                });
            }
        }
    }

    private _setTicketLinePromo(product: Product, qty: number): void {
        if (ProductFactory.Is(product, Phone) && product.promotions.automatic.length > 0) {
            const promosAmount = product.promotions.automatic.reduce(
                (total, promo) => total + promo.reductionAmount,
                0,
            );

            // to avoid showing line "remise immédiate" (if configurated) when the final price is less than 0 in case an automatic discount is applied
            const finalPriceAfterDiscount =
                (this.commercialProposal?.initialAmount || this.totals.daily.final) - this.refundAmount - promosAmount;

            if (promosAmount > 0 && finalPriceAfterDiscount > 0) {
                this.ticketLines.push({
                    qty,
                    label: 'Remise immédiate',
                    price: promosAmount,
                    type: 'daily',
                    isInstantDiscount: true,
                    gencode: product.gencode,
                });
            }
        }

        if (product.promotions.coupon) {
            this.ticketLines.push({
                qty,
                label: `Remise sur ${product.label}`,
                price: -product.promotions.coupon.reductionAmount,
                type: 'coupon',
                gencode: product.gencode,
            });
        }
    }

    private _getProductsTicket(): Product[] {
        const allProducts: Product[] = this.cartService.getAllProductsByType<Product>('Product');
        const mergedProducts = QuoteHelper.MergeProductByGencode(allProducts, false);
        return mergedProducts.filter((prod) => !ProductFactory.Is(prod, FaiBox));
    }

    private _getTotals(cartModel: CartModel): TotalsDetail {
        const priceDaily = {
            base: 0,
            final: 0,
        };
        const priceMonthly = {
            base: 0,
            final: 0,
        };
        const priceReported = {
            base: 0,
            final: 0,
        };

        cartModel.quotes.forEach((quote) => {
            quote.products.forEach((product) => {
                if (product.priceType === PRICE_TYPES.Today && product.prices.base > 0) {
                    priceDaily.base += product.prices.base || 0;
                    priceDaily.final += product.prices.final || 0;
                }

                if (product.priceType === PRICE_TYPES.EveryMonth && product.prices.base > 0) {
                    priceMonthly.base += this.priceInitialPipe.transform(product) || 0;
                    priceMonthly.final += product.prices.final || 0;
                }

                if (product.priceType === PRICE_TYPES.Reported && product.prices.base > 0) {
                    priceReported.base += product.prices.base || 0;
                    priceReported.final += product.prices.final || 0;
                }
            });
        });

        if (!this.isMixedCart && !this.oauth2Service.isConnected && this.promoHome?.amount) {
            priceMonthly.final -= this.promoHome?.amount;
        }
        // Quote totals
        return {
            daily: priceDaily,
            monthly: priceMonthly,
            reported: priceReported,
        };
    }

    private _initBreakpointMonitoring(): void {
        const mq: MediaQueryList = window.matchMedia(this.MOBILE_BREAKPOINT);
        this.isMobileMQ = mq.matches;

        mq.addEventListener('change', (evt) => {
            if (evt.matches !== this.isMobileMQ) {
                this.ngZone.run(() => {
                    this.isMobileMQ = evt?.matches;
                });
            }
        });
    }

    private _getRefundAmount(): number {
        const quote: QuoteModel = this.cartService.getQuote();

        let rsfAmount: number = 0;
        const odrAmount: number = quote.getProductsByType<Equipment>('Equipment').reduce((total, equipement) => {
            const amount: number = +equipement?.data?.odr?.amount || 0;
            return total + amount;
        }, 0);

        const phone: Phone = this.equipment as Phone;
        if (phone) {
            rsfAmount = phone.rsfAmount ?? 0;
        }

        return +(Number(odrAmount) + Number(rsfAmount)).toFixed(2);
    }

    private _getPopoverLabel(product: Product): string {
        if (ProductFactory.Is(product, Activation)) {
            return 'Ces frais permettent de couvrir le traitement de votre dossier, ainsi que divers frais administratifs et de gestion.';
        }

        if (ProductFactory.Is(product, Installation)) {
            return 'Ces frais couvrent la mise en service et la configuration de votre nouvelle ligne.';
        }

        if (ProductFactory.Is(product, OptionSalable) && this.isAcquisitionFix && this.faiService.isFmsToPayTodayByCategory((product.data as IOptionData).option_category)) {
            return 'Attention : toute annulation de souscription à la Bbox supprimera l’envoi de la clé 4G.';
        }

        return null;
    }

    private _getPromoAmount(): void {
        const { incitation: incitationPromo } = PromotionsService.GetPromotionsForProduct(
            this.plan,
            this.cartService.cartModel.promotions,
        );

        this.milanoPromoAmount =
            incitationPromo?.find((promo) => promo?.typesMarketing?.includes(TYPE_MARKETING.MULTILIGNES))?.amount || 0;
    }

    /**
     * Set monthly duration label
     * Take max duration if many exist
     */
    private _setMonthlyDurationLabel(): void {
        const products = this._getProductsTicket();
        const plansWithDuration = products.filter((p) => ProductFactory.Is(p, Plan) && p.prices?.duration);
        if (plansWithDuration?.length > 0) {
            this.monthlyDurationLabel = `Pendant ${Math.max.apply(
                null,
                plansWithDuration.map((p) => p.prices.duration),
            )} mois`;
        } else {
            this.monthlyDurationLabel = null;
        }
    }

    private _setEdpTicketLine(product: Product, productInfo: ProductLine): void {
        if (ProductFactory.Is(product, Edp)) {
            this.edpTicketLine = productInfo;
        }
    }

    private checkQuoteRenewal(): void {
        const quote: QuoteModel = this.cartService.getQuote();
        this.isRenewal = quote.isRenewal();
    }

    private _checkFmsToPayToday(product: Product): void {
        if (ProductFactory.Is(product, Activation) && this.fmsToPayToday) {
            product.priceType = PRICE_TYPES.Today;
        }
    }

    private refreshOptionsAndAccessories(): void {
        this.optionsTicketLines = this._getTicketLinesByProductType(['option_salable', 'offer_partner']);
        this.accessoriesTicketLines = this._getTicketLinesByProductType(['accessory']);
        this.mixedCartFaiOptions = [];
        this.mixedCartMobileOptions = [];

        if (this.isMixedCart) {
            const mobileQuoteProducts = this.getProductGencodeList(QUOTE_CONTEXTS.ACQUISITION);
            const faiQuoteProducts = this.getProductGencodeList(QUOTE_CONTEXTS.ACQUISITIONFIX);

            this.optionsTicketLines?.forEach((option) => {
                const isMobileOption = mobileQuoteProducts.includes(option.gencode);
                const isFaiOption = faiQuoteProducts.includes(option.gencode);

                if (isMobileOption) {
                    this.mixedCartMobileOptions.push(option);
                } else if (isFaiOption) {
                    this.mixedCartFaiOptions.push(option);
                }
            });
            this.showMobileOptions = this.mixedCartMobileOptions.length > 0;
            this.showFaiOptions = this.mixedCartFaiOptions.length > 0;
            return;
        }

        this.showAllOptions = this.optionsTicketLines.length > 0;

        if (this.accessoriesTicketLines.filter((accessory) => accessory.isIncludedPrice)?.length > 0) {
            this.showAccessoriesInDaily = false;
            this.showAccessoriesInFundings = true;
        } else {
            this.showAccessoriesInDaily = this.accessoriesTicketLines.length > 0;
            this.showAccessoriesInFundings = false;
        }
    }

    private getProductGencodeList(quoteContextId: QUOTE_CONTEXTS): string[] {
        const quote = this.cartService.cartModel.quotes?.find((q) => q.context.id === quoteContextId);

        if (!quote || !quote.products) {
            return [];
        }

        return quote.products.map((product) => product.gencode);
    }
}
